import { useMemo } from "react";
import { format } from "date-fns";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Dumbbell,
  Flame,
  Gauge,
  HeartPulse,
  MessageSquareText,
  Repeat,
  Timer,
  Trophy,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import type {
  AssignedWorkoutReview,
  TrackedSet,
  WorkoutAssignedExerciseReviewData,
  WorkoutAssignedGroupingReviewData,
} from "@/integrations/backend/workout-assigned";

type WorkoutReviewDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: AssignedWorkoutReview | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

const statusConfig: Record<AssignedWorkoutReview["completed_workout"]["status"], { label: string; className: string }> = {
  R: { label: "Ready", className: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" },
  S: { label: "Supercharged", className: "border-yellow-400/30 bg-yellow-400/10 text-yellow-100" },
  T: { label: "Tired", className: "border-orange-400/30 bg-orange-400/10 text-orange-100" },
  L: { label: "Low", className: "border-rose-400/30 bg-rose-400/10 text-rose-100" },
};

function hasLoggedValue(set: TrackedSet): boolean {
  return Boolean(
    (set.reps ?? 0) > 0 ||
      (set.weight_lbs ?? 0) > 0 ||
      (set.weight_kg ?? 0) > 0 ||
      (set.time_seconds ?? 0) > 0
  );
}

function formatDateTime(value: string | null): string {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not recorded";
  return format(parsed, "EEEE, MMM d • h:mm a");
}

function formatWorkoutDate(value: string | null): string {
  if (!value) return "Date unavailable";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "Date unavailable";
  return format(parsed, "EEEE, MMMM d");
}

function formatSeconds(seconds: number): string {
  if (seconds <= 0) return "0s";
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes === 0) return `${remainder}s`;
  if (remainder === 0) return `${minutes}m`;
  return `${minutes}m ${remainder}s`;
}

function getGroupingLabel(grouping: WorkoutAssignedGroupingReviewData): string {
  const exerciseCount = grouping.exercises.length;
  if (exerciseCount >= 3) return "Circuit";
  if (exerciseCount === 2) return "Superset";
  return "Exercise";
}

function buildPlannedSummary(exercise: WorkoutAssignedExerciseReviewData, grouping: WorkoutAssignedGroupingReviewData): string {
  const parts: string[] = [];
  const sets = exercise.sets || grouping.sets || 0;
  if (sets > 0) parts.push(`${sets} sets`);
  if (exercise.reps > 0) parts.push(`${exercise.reps} reps`);
  if (exercise.time_seconds > 0) parts.push(`${formatSeconds(exercise.time_seconds)} work`);
  if (exercise.weight_lbs > 0) parts.push(`${exercise.weight_lbs} lb target`);
  else if (exercise.weight_kg > 0) parts.push(`${exercise.weight_kg} kg target`);
  if (exercise.rest_seconds > 0) parts.push(`${formatSeconds(exercise.rest_seconds)} rest`);
  else if (grouping.rest_seconds > 0) parts.push(`${formatSeconds(grouping.rest_seconds)} rest`);
  return parts.length > 0 ? parts.join(" • ") : "Custom prescription";
}

export default function WorkoutReviewDrawer({
  open,
  onOpenChange,
  review,
  loading,
  error,
  onRetry,
}: WorkoutReviewDrawerProps) {
  const derivedStats = useMemo(() => {
    if (!review) {
      return { loggedSetCount: 0, totalVolumeLbs: 0, totalTimedWorkSeconds: 0 };
    }

    let loggedSetCount = 0;
    let totalVolumeLbs = 0;
    let totalTimedWorkSeconds = 0;

    for (const grouping of review.groupings) {
      for (const exercise of grouping.exercises) {
        for (const set of exercise.tracked_sets) {
          if (!hasLoggedValue(set)) continue;
          loggedSetCount += 1;
          if ((set.reps ?? 0) > 0 && (set.weight_lbs ?? 0) > 0) {
            totalVolumeLbs += (set.reps ?? 0) * (set.weight_lbs ?? 0);
          }
          if ((set.time_seconds ?? 0) > 0) {
            totalTimedWorkSeconds += set.time_seconds ?? 0;
          }
        }
      }
    }

    return { loggedSetCount, totalVolumeLbs, totalTimedWorkSeconds };
  }, [review]);

  const completedAt = review
    ? review.completed_workout.updated_at || review.completed_workout.created_at
    : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="border-primary/20 bg-dark-card text-white">
        <DrawerHeader className="text-left">
          <DrawerTitle>Workout Review</DrawerTitle>
          <DrawerDescription className="text-gray-light">
            Review your completed session, feedback, and logged sets.
          </DrawerDescription>
        </DrawerHeader>

        <div className="max-h-[78vh] space-y-4 overflow-y-auto px-4 pb-6">
          {loading ? (
            <div className="space-y-4">
              <div className="h-32 rounded-3xl bg-white/10 animate-pulse" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-24 rounded-2xl bg-white/10 animate-pulse" />
                <div className="h-24 rounded-2xl bg-white/10 animate-pulse" />
                <div className="h-24 rounded-2xl bg-white/10 animate-pulse" />
                <div className="h-24 rounded-2xl bg-white/10 animate-pulse" />
              </div>
              <div className="h-28 rounded-2xl bg-white/10 animate-pulse" />
              <div className="h-48 rounded-2xl bg-white/10 animate-pulse" />
            </div>
          ) : error ? (
            <Card className="border-red-400/20 bg-red-400/5">
              <CardContent className="space-y-3 p-4">
                <p className="font-medium text-white">Couldn’t load this workout review.</p>
                <p className="text-sm text-red-200/90">{error}</p>
                <Button onClick={onRetry} className="bg-primary text-black hover:bg-primary/90">
                  Try again
                </Button>
              </CardContent>
            </Card>
          ) : !review ? (
            <Card className="border-primary/20 bg-white/5">
              <CardContent className="p-4 text-sm text-gray-light">
                Select a completed workout to review it here.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="overflow-hidden border-primary/20 bg-white/5">
                <CardContent className="space-y-4 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary/90">
                          Completed Session
                        </p>
                      </div>
                      <h3 className="text-2xl font-semibold text-white">{review.name}</h3>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="border-primary/25 bg-black/20 text-white">
                          <CalendarDays className="mr-1 h-3.5 w-3.5" />
                          {formatWorkoutDate(review.date)}
                        </Badge>
                        <Badge variant="outline" className={statusConfig[review.completed_workout.status].className}>
                          {statusConfig[review.completed_workout.status].label}
                        </Badge>
                        <Badge variant="outline" className="border-primary/25 bg-black/20 text-white">
                          <Clock3 className="mr-1 h-3.5 w-3.5" />
                          {review.completed_workout.minutes} min logged
                        </Badge>
                      </div>
                    </div>
                    <div className="min-w-[180px] rounded-2xl border border-primary/15 bg-black/20 p-3">
                      <p className="text-xs text-gray-light">Completed</p>
                      <p className="mt-1 text-sm font-medium text-white">{formatDateTime(completedAt)}</p>
                    </div>
                  </div>

                  {review.description ? (
                    <p className="max-w-3xl text-sm leading-6 text-gray-light">{review.description}</p>
                  ) : null}
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2 text-gray-light">
                      <Clock3 className="h-4 w-4 text-primary" />
                      <span className="text-xs uppercase tracking-wide">Minutes</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">{review.completed_workout.minutes}</p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2 text-gray-light">
                      <Trophy className="h-4 w-4 text-primary" />
                      <span className="text-xs uppercase tracking-wide">Load Points</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">{review.completed_workout.points}</p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2 text-gray-light">
                      <Flame className="h-4 w-4 text-primary" />
                      <span className="text-xs uppercase tracking-wide">Intensity</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">
                      {review.completed_workout.intensity > 0 ? `${review.completed_workout.intensity}/10` : "—"}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2 text-gray-light">
                      <Gauge className="h-4 w-4 text-primary" />
                      <span className="text-xs uppercase tracking-wide">RPE</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">
                      {review.completed_workout.rpe !== null ? `${review.completed_workout.rpe}/10` : "—"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {(review.completed_workout.energy > 0 || review.category) && (
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="flex flex-wrap gap-2 p-4">
                    {review.completed_workout.energy > 0 ? (
                      <Badge variant="outline" className="border-primary/25 bg-black/20 text-white">
                        <HeartPulse className="mr-1 h-3.5 w-3.5" />
                        Energy {review.completed_workout.energy}/10
                      </Badge>
                    ) : null}
                    {review.category ? (
                      <Badge variant="outline" className="border-primary/25 bg-black/20 text-white">
                        {review.category}
                      </Badge>
                    ) : null}
                  </CardContent>
                </Card>
              )}

              <Card className="border-primary/20 bg-white/5">
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center gap-2">
                    <MessageSquareText className="h-4 w-4 text-primary" />
                    <h4 className="font-medium text-white">Feedback</h4>
                  </div>
                  {review.completed_workout.feedback_comments ? (
                    <p className="whitespace-pre-wrap text-sm leading-6 text-gray-light">
                      {review.completed_workout.feedback_comments}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-light">No notes were added for this workout.</p>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-light">Logged sets</p>
                    <p className="mt-2 text-2xl font-semibold text-white">{derivedStats.loggedSetCount}</p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-light">Total volume</p>
                    <p className="mt-2 text-2xl font-semibold text-white">
                      {derivedStats.totalVolumeLbs > 0 ? `${derivedStats.totalVolumeLbs.toLocaleString()} lb` : "—"}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-white/5">
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-light">Timed work</p>
                    <p className="mt-2 text-2xl font-semibold text-white">
                      {derivedStats.totalTimedWorkSeconds > 0 ? formatSeconds(derivedStats.totalTimedWorkSeconds) : "—"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                {review.groupings.map((grouping) => {
                  const groupingLabel = getGroupingLabel(grouping);
                  return (
                    <Card key={grouping.id} className="border-primary/20 bg-white/5">
                      <CardContent className="space-y-4 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-white">
                              {groupingLabel} {grouping.order ? `#${grouping.order}` : ""}
                            </p>
                            <p className="text-xs text-gray-light">
                              {grouping.sets > 0 ? `${grouping.sets} rounds` : "Custom rounds"}
                              {grouping.rest_seconds > 0 ? ` • ${formatSeconds(grouping.rest_seconds)} rest between rounds` : ""}
                            </p>
                          </div>
                          <Badge variant="outline" className="border-primary/25 bg-black/20 text-white">
                            {grouping.exercises.length} exercise{grouping.exercises.length === 1 ? "" : "s"}
                          </Badge>
                        </div>

                        <div className="space-y-3">
                          {grouping.exercises.map((exercise) => {
                            const loggedSets = exercise.tracked_sets.filter(hasLoggedValue);
                            return (
                              <div key={exercise.id} className="rounded-2xl border border-primary/10 bg-black/20 p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <h5 className="font-medium text-white">{exercise.exercise_name}</h5>
                                      {exercise.difficulty ? (
                                        <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                                          {exercise.difficulty}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    <p className="mt-1 text-sm text-gray-light">
                                      Planned: {buildPlannedSummary(exercise, grouping)}
                                    </p>
                                  </div>
                                  <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                    <Dumbbell className="mr-1 h-3.5 w-3.5" />
                                    {exercise.sets || grouping.sets || 0} sets
                                  </Badge>
                                </div>

                                {loggedSets.length > 0 ? (
                                  <div className="mt-4 grid gap-2">
                                    {loggedSets.map((set) => (
                                      <div
                                        key={set.id}
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/10 bg-white/5 px-3 py-2"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                            <Repeat className="mr-1 h-3 w-3" />
                                            Set {set.set_number}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                          {(set.reps ?? 0) > 0 ? (
                                            <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                              {set.reps} reps
                                            </Badge>
                                          ) : null}
                                          {(set.weight_lbs ?? 0) > 0 ? (
                                            <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                              {set.weight_lbs} lb
                                            </Badge>
                                          ) : null}
                                          {(set.weight_kg ?? 0) > 0 && (set.weight_lbs ?? 0) === 0 ? (
                                            <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                              {set.weight_kg} kg
                                            </Badge>
                                          ) : null}
                                          {(set.time_seconds ?? 0) > 0 ? (
                                            <Badge variant="outline" className="border-primary/20 bg-black/20 text-white">
                                              <Timer className="mr-1 h-3 w-3" />
                                              {formatSeconds(set.time_seconds ?? 0)}
                                            </Badge>
                                          ) : null}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="mt-4 text-sm text-gray-light">No set data logged for this exercise.</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
